// =============================================================================
// FILE: packages/renderer/src/hooks/useProjectFileTree.ts
// =============================================================================

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

export interface FileTreeFileNode {
  type: 'file';
  name: string; path: string; fileId: string;
  docType: string; discipline: string | null;
  revision: string | null; version: number; status: string;
  source: string; sizeBytes: number | null; lastModified: number;
}
export interface FileTreeDirNode {
  type: 'dir';
  name: string; path: string;
  children: FileTreeNode[]; fileCount: number;
}
export type FileTreeNode = FileTreeFileNode | FileTreeDirNode;

export interface NodeMeta {
  fileId: string; baseName: string; fileName: string; relativePath: string;
  checksum: string; version: number; status: string; source: string;
  docType: string; discipline: string | null; sheetNumber: string | null;
  revision: string | null; sizeBytes: number | null; lastModified: number;
  isOrphan: boolean; registeredAt: number; classifiedAt: number;
  meetingId: string | null; projectId: string | null;
}

export type PreviewKind = 'pdf' | 'image' | 'text' | 'json' | 'eml' | 'unsupported' | 'too_large';
export interface PreviewResult {
  kind: PreviewKind; mime: string; fileName: string; sizeBytes: number;
  base64?: string; text?: string; reason?: string;
}

export function useProjectFileTree(projectId: string | null) {
  const qc = useQueryClient();
  // Subscribe to push notifications so the panel auto-refreshes when files
  // land via FolderMonitor / DIE.
  useEffect(() => {
    const w = window as unknown as { electron?: { on?: (ch: string, cb: () => void) => () => void } };
    const off = w.electron?.on?.('pfe.treeChanged', () => {
      qc.invalidateQueries({ queryKey: ['pfe-tree'] });
    });
    return () => off?.();
  }, [qc]);

  return useQuery<FileTreeNode>({
    enabled: !!projectId,
    queryKey: ['pfe-tree', projectId],
    queryFn:  () => call(IPC.PFE_TREE, { projectId: projectId! }),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function useFileNodeMeta(fileId: string | null) {
  return useQuery<NodeMeta | null>({
    enabled: !!fileId,
    queryKey: ['pfe-meta', fileId],
    queryFn:  () => call(IPC.PFE_NODE_META, { fileId: fileId! }),
    staleTime: 60_000,
  });
}

export function useFilePreview(fileId: string | null) {
  return useQuery<PreviewResult>({
    enabled: !!fileId,
    queryKey: ['pfe-preview', fileId],
    queryFn:  () => call(IPC.PFE_PREVIEW, { fileId: fileId! }),
    staleTime: 60_000,
  });
}
